import React, { FC, useMemo } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { MinusOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useMediaQuery } from 'react-responsive';
import { useGetApplication } from '~/controllers/wizard';
import EditButton from '~/components/views/ShareholdersMultiple/EditButton';
import { hasAtLeastOneWarningField } from '~/utils/softDeclinesFields';
import { Table, Space, TextField, TableCardMobile } from '~/components';
import theme from '~/constants/theme';
import WithPep from '~/components/WithPep';
import { Draft } from '~/types/Draft';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { Application } from '~/types/Application';
import classNames from 'classnames';
import { getIndividualsFormName } from '~/pages/ksa/application/shareholders/components/individuals/IndividualEdit';
import { CheckThinIcon } from '~/img';
import colors from '~/constants/colors';
import longdash from '~/constants/longdash';
import { isCurrentUserByEmail } from '~/utils/getDecodedJwt';
import { WithCurrentUserIcon } from '~/components/CurrentUser';
import { stepName } from '~/pages/ksa/application/shareholders';

export type IndividualShareholderRowType = {
  uid?: string;
  email?: string;
  fullName?: string;
  hasPassport?: boolean;
  idNumber?: string;
  percent?: number;
  isAuthorizedSignatory?: boolean;
  isPep?: boolean;
  onEdit?: () => void;
};

interface Props {
  individuals: IndividualShareholderRowType[];
}

const IndividualTable: FC<Props> = (props) => {
  const { individuals } = props;

  const { t } = useTranslation('ksaApplication');
  const applicationQuery = useGetApplication();

  const individualColumns: ColumnsType<IndividualShareholderRowType> = useMemo(
    () => [
      {
        title: t('Full name'),
        dataIndex: 'fullName',
        key: 'fullName',
        render: (fullName: boolean, record) => {
          const isCurrentUser = isCurrentUserByEmail(record?.email);
          return <WithCurrentUserIcon showLabel={isCurrentUser}>{fullName}</WithCurrentUserIcon>;
        },
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon style={checkIconStyle} />;
          return <MinusOutlined style={checkIconStyle} />;
        },
      },
      {
        title: t('ID number'),
        dataIndex: 'idNumber',
        key: 'idNumber',
        render: (idNumber?: string) => idNumber || longdash,
      },
      {
        title: t('Shares, %'),
        dataIndex: 'percent',
        key: 'shares',
      },
      {
        title: t('Signatory'),
        dataIndex: 'isAuthorizedSignatory',
        key: 'isAuthorizedSignatory',
        render: (isAuthorizedSignatory: boolean) => {
          if (isAuthorizedSignatory) return t('Yes');
          return t('No');
        },
      },
      {
        title: <WithPep>{t('PEP')}</WithPep>,
        dataIndex: 'isPep',
        key: 'isPep',
        render: (isPep: boolean) => {
          if (isPep) return t('Yes');
          return t('No');
        },
      },
      {
        title: '',
        key: 'action',
        // fixed: 'right',
        render: (_, record) => (
          <Space size={16} style={actionsStyle}>
            <EditButton id="individualTable_EditButton" onClick={record.onEdit} />
          </Space>
        ),
      },
    ],
    [t]
  );
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  return (
    <Space size={24} direction="vertical">
      {!isEmpty(individuals) && (
        <>
          {!isTablet ? (
            <Table
              id="individualsTable"
              rowClassName={(_, index) =>
                classNames({
                  'ant-table-row-warning': hasAnyActiveWarning(applicationQuery.data, index),
                })
              }
              columns={individualColumns}
              dataSource={individuals}
              loading={applicationQuery.isLoading}
              pagination={pagination}
              rowKey="uid"
            />
          ) : (
            <Space size={12} direction="vertical">
              {individuals?.map((item, index) => {
                return (
                  <TableCardMobile
                    key={item.uid}
                    hasValidationWarnings={hasAnyActiveWarning(applicationQuery?.data, index)}
                    onEdit={item.onEdit}
                    fields={getIndividualShareholderCardContent(item)}
                  />
                );
              })}
            </Space>
          )}
        </>
      )}
    </Space>
  );
};

const pagination = { hideOnSinglePage: true };

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };
const checkIconStyle = { color: colors.blue['1100'] };

const getIndividualShareholderCardContent = (record: IndividualShareholderRowType) => {
  const t = i18next.getFixedT(null, 'ksaApplication');
  const { fullName, isPep, idNumber, hasPassport, percent, isAuthorizedSignatory, email } =
    record ?? {};
  const isCurrentUser = isCurrentUserByEmail(email);

  return [
    <TextField key="fullName" label={t('Full Name')}>
      <WithCurrentUserIcon showLabel={isCurrentUser}>{fullName}</WithCurrentUserIcon>;
    </TextField>,
    <TextField key="hasPassport" label={t('Passport')}>
      {hasPassport ? (
        <CheckThinIcon style={checkIconStyle} />
      ) : (
        <MinusOutlined style={checkIconStyle} />
      )}
    </TextField>,
    <TextField key="idNumber" label={t('Document number')}>
      {idNumber}
    </TextField>,
    <TextField key="shares" label={t('Shares, %')}>
      {percent}
    </TextField>,
    <TextField key="signatory" label={t('Signatory')}>
      <>{isAuthorizedSignatory ? t('Yes') : t('No')}</>
    </TextField>,
    <TextField key="isPep" label={t('PEP')}>
      <>{isPep ? t('Yes') : t('No')}</>
    </TextField>,
  ];
};

const hasAnyActiveWarning = (application: Application | undefined, index: number) =>
  hasAtLeastOneWarningField(application, getIndividualsFormName(index).join('.'));

export const getIndividualsData = (
  shareholdersData: Draft<'shareholders'> | undefined,
  openEdit: (uid: string) => void
): IndividualShareholderRowType[] => {
  const individuals = (shareholdersData?.shareholders as KsaShareholders)?.individuals;
  return (
    individuals?.map((item) => {
      return {
        uid: item.uid,
        email: item.personalData?.email,
        fullName: item?.personalData?.fullName,
        hasPassport: !isEmpty(item?.passport?.documentNumber),
        idNumber: item?.personalDocument?.idNumber,
        percent: item?.ownershipShare?.percent,
        isAuthorizedSignatory: item?.personalData?.isAuthorizedSignatory,
        isPep: item?.personalData?.isPep,
        onEdit: () => item?.uid && openEdit(item.uid),
      };
    }) || []
  );
};

export default IndividualTable;
